import React, { useEffect, useMemo, useState } from "react";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Autocomplete,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Grid,
	IconButton,
	TextField,
	Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";

import globalAxios from "../../../globalAxios.js";
import { SERVER_URL } from "../../../config.js";
import ListboxComponent from "../../../components/listbox/ListBox.tsx";
import { useAppDispatch, useAppSelector } from "../../../store/hooks.ts";
import {
	addSurgeonBillingRule,
	removeSurgeonBillingRule,
	selectLocationRulesForSurgeon,
	selectSurgeonBillingRulesForSurgeon,
	selectSurgeonBillingRulesLoading,
	selectTimelineRulesForSurgeon,
	setSelectedSurgeonId,
	setSurgeonBillingRulesError,
	setSurgeonBillingRulesForSurgeon,
	setSurgeonBillingRulesLoading,
	updateSurgeonBillingRule,
	type SurgeonBillingRule,
} from "../../../store/slices/surgeonBillingRulesSlice.ts";

type Surgeon = {
	id: number;
	first_name: string;
	last_name: string;
};

type Hospital = {
	id: number;
	name: string;
};

type RuleDraft = {
	id: string;
	surgeon_id: number | null;
	hospital: Hospital | null;
	hospital_id: number | null;
	start_date: string | null;
	end_date: string | null;
	default_oop: string | null;
	ifc_required: boolean;
};

const createId = () => {
	if (typeof crypto !== "undefined" && crypto.randomUUID) {
		return crypto.randomUUID();
	}

	return `${Date.now()}-${Math.random()}`;
};

const getSurgeonName = (surgeon: Surgeon) =>
	`${surgeon.first_name || ""} ${surgeon.last_name || ""}`.trim();

const isValidMoneyInput = (value: string) => /^\d*(\.\d{0,2})?$/.test(value);

const getTime = (date: string | null) => {
	if (!date) return null;
	return new Date(date).getTime();
};

const subtractOneDay = (date: string) => {
	const d = new Date(date);
	d.setDate(d.getDate() - 1);
	return d.toISOString().slice(0, 10);
};

const locationScopesConflict = (
	a: Hospital | null | undefined,
	b: Hospital | null | undefined,
): boolean => {
	if (!a || !b) return true;
	return a.id === b.id;
};

const datesOverlap = (
	aStart: string | null,
	aEnd: string | null,
	bStart: string | null,
	bEnd: string | null,
): boolean => {
	const aStartTime = getTime(aStart);
	const bStartTime = getTime(bStart);

	if (aStartTime === null || bStartTime === null) return false;

	const aEndTime = getTime(aEnd) ?? Number.POSITIVE_INFINITY;
	const bEndTime = getTime(bEnd) ?? Number.POSITIVE_INFINITY;

	return aStartTime <= bEndTime && bStartTime <= aEndTime;
};

const getRuleHospital = (rule: SurgeonBillingRule | RuleDraft) =>
	rule.hospital ?? null;

const getTimelineOverlapIds = (rules: SurgeonBillingRule[]) => {
	const overlapIds = new Set<number | string>();

	for (let i = 0; i < rules.length; i += 1) {
		for (let j = i + 1; j < rules.length; j += 1) {
			const a = rules[i];
			const b = rules[j];

			const overlaps =
				locationScopesConflict(
					getRuleHospital(a),
					getRuleHospital(b),
				) &&
				datesOverlap(
					a.start_date,
					a.end_date,
					b.start_date,
					b.end_date,
				);

			if (overlaps) {
				overlapIds.add(a.id);
				overlapIds.add(b.id);
			}
		}
	}

	return overlapIds;
};

const makeEmptyTimelineDraft = (surgeonId: number | null): RuleDraft => ({
	id: createId(),
	surgeon_id: surgeonId,
	start_date: "",
	end_date: null,
	hospital: null,
	hospital_id: null,
	default_oop: "",
	ifc_required: false,
});

const makeEmptyLocationDraft = (surgeonId: number | null): RuleDraft => ({
	id: createId(),
	surgeon_id: surgeonId,
	start_date: null,
	end_date: null,
	hospital: null,
	hospital_id: null,
	default_oop: "",
	ifc_required: false,
});

const normaliseRulePayload = (rule: RuleDraft | SurgeonBillingRule) => ({
	surgeon_id: rule.surgeon_id,
	hospital_id: rule.hospital?.id ?? rule.hospital_id ?? null,
	start_date: rule.start_date || null,
	end_date: rule.end_date || null,
	default_oop:
		rule.default_oop === "" || rule.default_oop === undefined
			? null
			: rule.default_oop,
	ifc_required: !!rule.ifc_required,
});

const SurgeonBillingRules = () => {
	const dispatch = useAppDispatch();

	const [surgeons, setSurgeons] = useState<Surgeon[]>([]);
	const [hospitals, setHospitals] = useState<Hospital[]>([]);
	const [selectedSurgeon, setSelectedSurgeon] = useState<Surgeon | null>(
		null,
	);

	const selectedSurgeonId = selectedSurgeon?.id ?? null;

	const allRules = useAppSelector(
		selectSurgeonBillingRulesForSurgeon(selectedSurgeonId),
	);
	const timelineRules = useAppSelector(
		selectTimelineRulesForSurgeon(selectedSurgeonId),
	);
	const locationRules = useAppSelector(
		selectLocationRulesForSurgeon(selectedSurgeonId),
	);
	const rulesLoading = useAppSelector(
		selectSurgeonBillingRulesLoading(selectedSurgeonId),
	);

	const [loadingSurgeons, setLoadingSurgeons] = useState(false);
	const [loadingHospitals, setLoadingHospitals] = useState(false);

	const [savingRule, setSavingRule] = useState(false);
	const [deletingRuleId, setDeletingRuleId] = useState<
		number | string | null
	>(null);

	const [timelineAddOpen, setTimelineAddOpen] = useState(false);
	const [locationAddOpen, setLocationAddOpen] = useState(false);

	const [editingTimelineRuleId, setEditingTimelineRuleId] = useState<
		number | string | null
	>(null);
	const [editingLocationRuleId, setEditingLocationRuleId] = useState<
		number | string | null
	>(null);

	const [editingDraftsById, setEditingDraftsById] = useState<
		Record<string, RuleDraft>
	>({});

	const [newTimelineRule, setNewTimelineRule] = useState<RuleDraft>(
		makeEmptyTimelineDraft(null),
	);

	const [newLocationRule, setNewLocationRule] = useState<RuleDraft>(
		makeEmptyLocationDraft(null),
	);

	const overlappingTimelineIds = useMemo(
		() => getTimelineOverlapIds(timelineRules),
		[timelineRules],
	);

	const sortedTimelineRules = useMemo(() => {
		return [...timelineRules].sort((a, b) => {
			const aTime = getTime(a.start_date) ?? 0;
			const bTime = getTime(b.start_date) ?? 0;
			return aTime - bTime;
		});
	}, [timelineRules]);

	useEffect(() => {
		const fetchDropdownData = async () => {
			setLoadingSurgeons(true);
			setLoadingHospitals(true);

			try {
				const [surgeonRes, hospitalRes] = await Promise.all([
					globalAxios.get(`${SERVER_URL}/surgeons`),
					globalAxios.get(`${SERVER_URL}/hospitals`),
				]);

				console.log("[DEBUG] /surgeons response:", surgeonRes.data);
				console.log("[DEBUG] /hospitals response:", hospitalRes.data);

				setSurgeons(
					Array.isArray(surgeonRes.data?.surgeons)
						? surgeonRes.data.surgeons
						: [],
				);

				setHospitals(
					Array.isArray(hospitalRes.data?.hospitals)
						? hospitalRes.data.hospitals
						: [],
				);
			} catch (error) {
				console.error("[ERROR] failed to fetch dropdown data:", error);
				setSurgeons([]);
				setHospitals([]);
			} finally {
				setLoadingSurgeons(false);
				setLoadingHospitals(false);
			}
		};

		fetchDropdownData();
	}, []);

	useEffect(() => {
		if (!selectedSurgeonId) return;

		const fetchRules = async () => {
			dispatch(
				setSurgeonBillingRulesLoading({
					surgeonId: selectedSurgeonId,
					loading: true,
				}),
			);

			try {
				console.log("[DEBUG] fetching surgeon billing rules", {
					surgeonId: selectedSurgeonId,
				});

				const response = await globalAxios.get(
					`${SERVER_URL}/surgeon-billing-rules/${selectedSurgeonId}`,
				);

				console.log(
					"[DEBUG] surgeon billing rules response:",
					response.data,
				);

				dispatch(
					setSurgeonBillingRulesForSurgeon({
						surgeonId: selectedSurgeonId,
						rules: Array.isArray(response.data?.rules)
							? response.data.rules
							: [],
					}),
				);

				dispatch(setSurgeonBillingRulesError(null));
			} catch (error) {
				console.error(
					"[ERROR] failed to fetch surgeon billing rules:",
					error,
				);
				dispatch(
					setSurgeonBillingRulesError(
						"Failed to fetch surgeon billing rules.",
					),
				);
			} finally {
				dispatch(
					setSurgeonBillingRulesLoading({
						surgeonId: selectedSurgeonId,
						loading: false,
					}),
				);
			}
		};

		fetchRules();
	}, [selectedSurgeonId, dispatch]);

	useEffect(() => {
		setNewTimelineRule(makeEmptyTimelineDraft(selectedSurgeonId));
		setNewLocationRule(makeEmptyLocationDraft(selectedSurgeonId));
	}, [selectedSurgeonId]);

	const newTimelineWouldOverlap = useMemo(() => {
		if (!newTimelineRule.start_date) return false;

		const adjustedRules = timelineRules.map((rule) => {
			const sameExactLocation =
				(rule.hospital?.id ?? null) ===
				(newTimelineRule.hospital?.id ?? null);

			const newRuleStartsAfter =
				rule.start_date &&
				getTime(newTimelineRule.start_date)! >
					getTime(rule.start_date)!;

			if (sameExactLocation && !rule.end_date && newRuleStartsAfter) {
				return {
					...rule,
					end_date: subtractOneDay(newTimelineRule.start_date),
				};
			}

			return rule;
		});

		return adjustedRules.some(
			(rule) =>
				locationScopesConflict(
					rule.hospital,
					newTimelineRule.hospital,
				) &&
				datesOverlap(
					rule.start_date,
					rule.end_date,
					newTimelineRule.start_date,
					newTimelineRule.end_date,
				),
		);
	}, [newTimelineRule, timelineRules]);

	const canAddTimelineRule =
		!!selectedSurgeonId &&
		!!newTimelineRule.start_date &&
		!newTimelineWouldOverlap;

	const canAddLocationRule =
		!!selectedSurgeonId && !!newLocationRule.hospital;

	const handleNewTimelineChange = (
		key: keyof RuleDraft,
		value: string | boolean | Hospital | null,
	) => {
		console.log("[DEBUG] updating new timeline rule:", { key, value });

		setNewTimelineRule((prev) => ({
			...prev,
			[key]: value,
			...(key === "hospital"
				? { hospital_id: (value as Hospital | null)?.id ?? null }
				: {}),
		}));
	};

	const handleNewLocationChange = (
		key: keyof RuleDraft,
		value: string | boolean | Hospital | null,
	) => {
		console.log("[DEBUG] updating new location rule:", { key, value });

		setNewLocationRule((prev) => ({
			...prev,
			[key]: value,
			...(key === "hospital"
				? { hospital_id: (value as Hospital | null)?.id ?? null }
				: {}),
		}));
	};

	const createEditingDraft = (rule: SurgeonBillingRule): RuleDraft => ({
		id: String(rule.id),
		surgeon_id: rule.surgeon_id,
		hospital: rule.hospital ?? null,
		hospital_id: rule.hospital_id ?? rule.hospital?.id ?? null,
		start_date: rule.start_date,
		end_date: rule.end_date,
		default_oop: rule.default_oop ?? "",
		ifc_required: !!rule.ifc_required,
	});

	const getEditingDraft = (rule: SurgeonBillingRule): RuleDraft => {
		return editingDraftsById[String(rule.id)] ?? createEditingDraft(rule);
	};

	const setEditingDraftValue = (
		ruleId: number | string,
		key: keyof RuleDraft,
		value: string | boolean | Hospital | null,
	) => {
		console.log("[DEBUG] updating editing draft:", { ruleId, key, value });

		setEditingDraftsById((prev) => {
			const existing =
				prev[String(ruleId)] ??
				createEditingDraft(
					allRules.find((rule) => rule.id === ruleId)!,
				);

			return {
				...prev,
				[String(ruleId)]: {
					...existing,
					[key]: value,
					...(key === "hospital"
						? {
								hospital_id:
									(value as Hospital | null)?.id ?? null,
							}
						: {}),
				},
			};
		});
	};

	const handleAddTimelineRule = async () => {
		console.log(
			"[DEBUG] attempting to add timeline rule:",
			newTimelineRule,
		);

		if (!canAddTimelineRule || !selectedSurgeonId) {
			console.warn("[WARN] timeline rule not added due to validation");
			return;
		}

		setSavingRule(true);

		try {
			const newRuleHospitalId =
				newTimelineRule.hospital?.id ??
				newTimelineRule.hospital_id ??
				null;

			const previousOpenEndedRulesToClose = timelineRules.filter(
				(rule) => {
					const sameLocation =
						(rule.hospital_id ?? rule.hospital?.id ?? null) ===
						newRuleHospitalId;

					const previousHasNoEnd = !rule.end_date;

					const newStartsAfterPrevious =
						rule.start_date &&
						newTimelineRule.start_date &&
						new Date(newTimelineRule.start_date).getTime() >
							new Date(rule.start_date).getTime();

					return (
						sameLocation &&
						previousHasNoEnd &&
						newStartsAfterPrevious
					);
				},
			);

			console.log("[DEBUG] previous open-ended rules to close:", {
				count: previousOpenEndedRulesToClose.length,
				rules: previousOpenEndedRulesToClose,
			});

			for (const rule of previousOpenEndedRulesToClose) {
				const newEndDate = subtractOneDay(newTimelineRule.start_date!);

				const updatePayload = {
					surgeon_id: rule.surgeon_id,
					hospital_id: rule.hospital_id ?? rule.hospital?.id ?? null,
					start_date: rule.start_date,
					end_date: newEndDate,
					default_oop: rule.default_oop,
					ifc_required: rule.ifc_required,
				};

				console.log("[DEBUG] auto-closing previous timeline rule:", {
					ruleId: rule.id,
					updatePayload,
				});

				const closeResponse = await globalAxios.put(
					`${SERVER_URL}/surgeon-billing-rules/${rule.id}`,
					updatePayload,
				);

				console.log("[DEBUG] auto-close response:", closeResponse.data);

				dispatch(
					updateSurgeonBillingRule({
						surgeonId: selectedSurgeonId,
						ruleId: rule.id,
						updates: closeResponse.data?.rule ?? {
							end_date: newEndDate,
						},
					}),
				);
			}

			const payload = normaliseRulePayload({
				...newTimelineRule,
				surgeon_id: selectedSurgeonId,
			});

			console.log("[DEBUG] POST timeline rule payload:", payload);

			const response = await globalAxios.post(
				`${SERVER_URL}/surgeon-billing-rules`,
				payload,
			);

			console.log("[DEBUG] created timeline rule:", response.data);

			if (Array.isArray(response.data?.rules)) {
				dispatch(
					setSurgeonBillingRulesForSurgeon({
						surgeonId: selectedSurgeonId,
						rules: response.data.rules,
					}),
				);
			} else if (response.data?.rule) {
				dispatch(addSurgeonBillingRule(response.data.rule));
			}

			setNewTimelineRule(makeEmptyTimelineDraft(selectedSurgeonId));
			setTimelineAddOpen(false);
		} catch (error) {
			console.error("[ERROR] failed to add timeline rule:", error);
			dispatch(
				setSurgeonBillingRulesError("Failed to add timeline rule."),
			);
		} finally {
			setSavingRule(false);
		}
	};

	const handleAddLocationRule = async () => {
		console.log(
			"[DEBUG] attempting to add location rule:",
			newLocationRule,
		);

		if (!canAddLocationRule || !selectedSurgeonId) {
			console.warn("[WARN] location rule not added due to validation");
			return;
		}

		setSavingRule(true);

		try {
			const payload = normaliseRulePayload({
				...newLocationRule,
				surgeon_id: selectedSurgeonId,
				start_date: null,
				end_date: null,
			});

			console.log("[DEBUG] POST location rule payload:", payload);

			const response = await globalAxios.post(
				`${SERVER_URL}/surgeon-billing-rules`,
				payload,
			);

			console.log("[DEBUG] created location rule:", response.data);

			if (response.data?.rule) {
				dispatch(addSurgeonBillingRule(response.data.rule));
			}

			setNewLocationRule(makeEmptyLocationDraft(selectedSurgeonId));
			setLocationAddOpen(false);
		} catch (error) {
			console.error("[ERROR] failed to add location rule:", error);
			dispatch(
				setSurgeonBillingRulesError("Failed to add location rule."),
			);
		} finally {
			setSavingRule(false);
		}
	};

	const handleSaveEditedRule = async (rule: SurgeonBillingRule) => {
		if (!selectedSurgeonId) return;

		const draft = getEditingDraft(rule);

		setSavingRule(true);

		try {
			const payload = normaliseRulePayload(draft);

			console.log("[DEBUG] PUT rule payload:", {
				ruleId: rule.id,
				payload,
			});

			const response = await globalAxios.put(
				`${SERVER_URL}/surgeon-billing-rules/${rule.id}`,
				payload,
			);

			console.log("[DEBUG] updated rule response:", response.data);

			dispatch(
				updateSurgeonBillingRule({
					surgeonId: selectedSurgeonId,
					ruleId: rule.id,
					updates: response.data?.rule ?? payload,
				}),
			);

			setEditingDraftsById((prev) => {
				const copy = { ...prev };
				delete copy[String(rule.id)];
				return copy;
			});

			setEditingTimelineRuleId(null);
			setEditingLocationRuleId(null);
		} catch (error) {
			console.error("[ERROR] failed to update rule:", error);
			dispatch(setSurgeonBillingRulesError("Failed to update rule."));
		} finally {
			setSavingRule(false);
		}
	};

	const handleDeleteRule = async (rule: SurgeonBillingRule) => {
		if (!selectedSurgeonId) return;

		setDeletingRuleId(rule.id);

		try {
			console.log("[DEBUG] DELETE surgeon billing rule:", rule.id);

			await globalAxios.delete(
				`${SERVER_URL}/surgeon-billing-rules/${rule.id}`,
			);

			dispatch(
				removeSurgeonBillingRule({
					surgeonId: selectedSurgeonId,
					ruleId: rule.id,
				}),
			);
		} catch (error) {
			console.error("[ERROR] failed to delete rule:", error);
			dispatch(setSurgeonBillingRulesError("Failed to delete rule."));
		} finally {
			setDeletingRuleId(null);
		}
	};

	const renderHospitalAutocomplete = ({
		value,
		onChange,
		label,
		helperText,
	}: {
		value: Hospital | null;
		onChange: (value: Hospital | null) => void;
		label: string;
		helperText?: string;
	}) => (
		<Autocomplete
			size="small"
			disableListWrap
			loading={loadingHospitals}
			options={hospitals}
			value={value}
			onChange={(_, newValue) => onChange(newValue)}
			getOptionLabel={(option) => option.name || ""}
			isOptionEqualToValue={(option, selected) =>
				option.id === selected.id
			}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					helperText={helperText}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{loadingHospitals ? (
									<CircularProgress size={18} />
								) : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
			renderOption={(props, option) => (
				<li {...props} key={option.id}>
					{option.name}
				</li>
			)}
			ListboxComponent={ListboxComponent}
		/>
	);

	const renderRuleForm = ({
		rule,
		onChange,
		isTimeline,
		isNew,
	}: {
		rule: RuleDraft;
		onChange: (
			key: keyof RuleDraft,
			value: string | boolean | Hospital | null,
		) => void;
		isTimeline: boolean;
		isNew: boolean;
	}) => (
		<Grid container spacing={2}>
			{isTimeline && (
				<>
					<Grid item xs={12} md={6}>
						<TextField
							label="Start Date"
							type="date"
							size="small"
							fullWidth
							value={rule.start_date || ""}
							onChange={(e) =>
								onChange("start_date", e.target.value)
							}
							InputLabelProps={{ shrink: true }}
							required
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						<TextField
							label="End Date"
							type="date"
							size="small"
							fullWidth
							value={rule.end_date || ""}
							onChange={(e) =>
								onChange("end_date", e.target.value || null)
							}
							InputLabelProps={{ shrink: true }}
							helperText="Blank means no end"
						/>
					</Grid>
				</>
			)}

			<Grid item xs={12}>
				{renderHospitalAutocomplete({
					value: rule.hospital,
					onChange: (value) => onChange("hospital", value),
					label: isTimeline ? "Hospital / Location" : "Hospital",
					helperText: isTimeline
						? "Leave blank to apply to all locations."
						: undefined,
				})}
			</Grid>

			<Grid item xs={12}>
				<TextField
					label="Default OOP"
					size="small"
					fullWidth
					value={rule.default_oop || ""}
					onChange={(e) => {
						if (!isValidMoneyInput(e.target.value)) return;
						onChange("default_oop", e.target.value);
					}}
					inputProps={{ inputMode: "decimal" }}
					helperText="Leave blank for no OOP / no gap."
				/>
			</Grid>

			<Grid item xs={12}>
				<FormControlLabel
					control={
						<Checkbox
							checked={rule.ifc_required}
							onChange={(e) =>
								onChange("ifc_required", e.target.checked)
							}
						/>
					}
					label="IFC required"
				/>
			</Grid>

			{isNew && isTimeline && newTimelineWouldOverlap && (
				<Grid item xs={12}>
					<Typography variant="body2" color="error">
						This timeline rule overlaps an existing rule for the
						same location scope.
					</Typography>
				</Grid>
			)}
		</Grid>
	);

	return (
		<Box>
			<Typography variant="h6" sx={{ mb: 2 }}>
				Surgeon Billing Rules
			</Typography>

			<Autocomplete
				size="small"
				disableListWrap
				loading={loadingSurgeons}
				options={surgeons}
				value={selectedSurgeon}
				onChange={(_, newValue) => {
					console.log("[DEBUG] selected surgeon:", newValue);

					setSelectedSurgeon(newValue);
					dispatch(setSelectedSurgeonId(newValue?.id ?? null));

					setTimelineAddOpen(false);
					setLocationAddOpen(false);
					setEditingTimelineRuleId(null);
					setEditingLocationRuleId(null);
					setEditingDraftsById({});
				}}
				getOptionLabel={(option) => getSurgeonName(option)}
				isOptionEqualToValue={(option, value) => option.id === value.id}
				renderInput={(params) => (
					<TextField
						{...params}
						label="Select Surgeon"
						sx={{ maxWidth: 400 }}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{loadingSurgeons ? (
										<CircularProgress size={18} />
									) : null}
									{params.InputProps.endAdornment}
								</>
							),
						}}
					/>
				)}
				renderOption={(props, option) => (
					<li {...props} key={option.id}>
						{getSurgeonName(option)}
					</li>
				)}
				ListboxComponent={ListboxComponent}
			/>

			{selectedSurgeon && (
				<Grid container spacing={2} sx={{ mt: 3 }}>
					<Grid item xs={12} md={6}>
						<Box
							sx={{
								border: "1px solid",
								borderColor: "divider",
								borderRadius: 2,
								p: 2,
								height: "100%",
							}}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									mb: 2,
								}}
							>
								<Box>
									<Typography variant="h6">
										Timeline Rules
									</Typography>
									<Typography
										variant="body2"
										color="text.secondary"
									>
										Chronological rule blocks.
									</Typography>
								</Box>

								<Button
									size="small"
									variant="contained"
									disableElevation
									onClick={() =>
										setTimelineAddOpen((prev) => !prev)
									}
									disabled={rulesLoading}
								>
									{timelineAddOpen ? "Close" : "Add"}
								</Button>
							</Box>

							{timelineAddOpen && (
								<Accordion
									expanded
									disableGutters
									sx={{
										mb: 2,
										border: "1px solid",
										borderColor: "primary.main",
										boxShadow: "none",
										"&:before": { display: "none" },
									}}
								>
									<AccordionSummary
										expandIcon={<ExpandMoreIcon />}
									>
										<Typography fontWeight="bold">
											Add Timeline Rule
										</Typography>
									</AccordionSummary>

									<AccordionDetails>
										{renderRuleForm({
											rule: newTimelineRule,
											onChange: handleNewTimelineChange,
											isTimeline: true,
											isNew: true,
										})}

										<Box sx={{ mt: 2, textAlign: "right" }}>
											<Button
												variant="contained"
												disableElevation
												size="small"
												onClick={handleAddTimelineRule}
												disabled={
													!canAddTimelineRule ||
													savingRule
												}
											>
												Add Rule
											</Button>
										</Box>
									</AccordionDetails>
								</Accordion>
							)}

							{rulesLoading && (
								<Typography
									variant="body2"
									color="text.secondary"
								>
									Loading rules...
								</Typography>
							)}

							{!rulesLoading && timelineRules.length === 0 && (
								<Typography
									variant="body2"
									color="text.secondary"
								>
									No timeline rules yet.
								</Typography>
							)}

							{sortedTimelineRules.map((rule, index) => {
								const hasOverlap = overlappingTimelineIds.has(
									rule.id,
								);
								const isEditing =
									editingTimelineRuleId === rule.id;
								const draft = getEditingDraft(rule);

								return (
									<Box
										key={rule.id}
										sx={{
											border: "1px solid",
											borderColor: hasOverlap
												? "error.main"
												: "divider",
											borderRadius: 2,
											p: 2,
											mb: 2,
											backgroundColor: "grey.100",
										}}
									>
										<Box
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												mb: 1,
											}}
										>
											<Typography
												variant="subtitle2"
												sx={{ fontWeight: "bold" }}
											>
												Timeline Rule {index + 1}
											</Typography>

											<IconButton
												size="small"
												onClick={() => {
													if (isEditing) {
														setEditingTimelineRuleId(
															null,
														);
														return;
													}

													setEditingDraftsById(
														(prev) => ({
															...prev,
															[String(rule.id)]:
																createEditingDraft(
																	rule,
																),
														}),
													);
													setEditingTimelineRuleId(
														rule.id,
													);
												}}
											>
												<EditIcon fontSize="small" />
											</IconButton>
										</Box>

										{isEditing ? (
											<>
												{renderRuleForm({
													rule: draft,
													onChange: (key, value) =>
														setEditingDraftValue(
															rule.id,
															key,
															value,
														),
													isTimeline: true,
													isNew: false,
												})}

												<Box
													sx={{
														mt: 2,
														textAlign: "right",
														display: "flex",
														gap: 1,
														justifyContent:
															"flex-end",
													}}
												>
													<Button
														size="small"
														color="error"
														onClick={() =>
															handleDeleteRule(
																rule,
															)
														}
														disabled={
															deletingRuleId ===
															rule.id
														}
													>
														Delete
													</Button>

													<Button
														size="small"
														variant="contained"
														disableElevation
														onClick={() =>
															handleSaveEditedRule(
																rule,
															)
														}
														disabled={savingRule}
													>
														Save
													</Button>
												</Box>
											</>
										) : (
											<>
												<Typography variant="body2">
													<strong>Period:</strong>{" "}
													{rule.start_date || "—"} →{" "}
													{rule.end_date || "No end"}
												</Typography>

												<Typography variant="body2">
													<strong>Location:</strong>{" "}
													{rule.hospital?.name ||
														"All locations"}
												</Typography>

												<Typography variant="body2">
													<strong>
														Default OOP:
													</strong>{" "}
													{rule.default_oop
														? `$${rule.default_oop}`
														: "No OOP / No gap"}
												</Typography>

												<Typography variant="body2">
													<strong>
														IFC required:
													</strong>{" "}
													{rule.ifc_required
														? "Yes"
														: "No"}
												</Typography>
											</>
										)}

										{hasOverlap && (
											<Typography
												variant="body2"
												color="error"
												sx={{ mt: 1 }}
											>
												This rule overlaps another
												timeline rule with the same
												location scope.
											</Typography>
										)}
									</Box>
								);
							})}
						</Box>
					</Grid>

					<Grid item xs={12} md={6}>
						<Box
							sx={{
								border: "1px solid",
								borderColor: "divider",
								borderRadius: 2,
								p: 2,
								height: "100%",
							}}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									mb: 2,
								}}
							>
								<Box>
									<Typography variant="h6">
										Location Rules
									</Typography>
									<Typography
										variant="body2"
										color="text.secondary"
									>
										Timeless location-specific exceptions.
									</Typography>
								</Box>

								<Button
									size="small"
									variant="contained"
									disableElevation
									onClick={() =>
										setLocationAddOpen((prev) => !prev)
									}
									disabled={rulesLoading}
								>
									{locationAddOpen ? "Close" : "Add"}
								</Button>
							</Box>

							{locationAddOpen && (
								<Accordion
									expanded
									disableGutters
									sx={{
										mb: 2,
										border: "1px solid",
										borderColor: "primary.main",
										boxShadow: "none",
										"&:before": { display: "none" },
									}}
								>
									<AccordionSummary
										expandIcon={<ExpandMoreIcon />}
									>
										<Typography fontWeight="bold">
											Add Location Rule
										</Typography>
									</AccordionSummary>

									<AccordionDetails>
										{renderRuleForm({
											rule: newLocationRule,
											onChange: handleNewLocationChange,
											isTimeline: false,
											isNew: true,
										})}

										<Box sx={{ mt: 2, textAlign: "right" }}>
											<Button
												variant="contained"
												disableElevation
												size="small"
												onClick={handleAddLocationRule}
												disabled={
													!canAddLocationRule ||
													savingRule
												}
											>
												Add Rule
											</Button>
										</Box>
									</AccordionDetails>
								</Accordion>
							)}

							{!rulesLoading && locationRules.length === 0 && (
								<Typography
									variant="body2"
									color="text.secondary"
								>
									No location rules yet.
								</Typography>
							)}

							{locationRules.map((rule, index) => {
								const isEditing =
									editingLocationRuleId === rule.id;
								const draft = getEditingDraft(rule);

								return (
									<Box
										key={rule.id}
										sx={{
											border: "1px solid",
											borderColor: "divider",
											borderRadius: 2,
											p: 2,
											mb: 2,
											backgroundColor: "grey.100",
										}}
									>
										<Box
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												mb: 1,
											}}
										>
											<Typography
												variant="subtitle2"
												sx={{ fontWeight: "bold" }}
											>
												Location Rule {index + 1}
											</Typography>

											<IconButton
												size="small"
												onClick={() => {
													if (isEditing) {
														setEditingLocationRuleId(
															null,
														);
														return;
													}

													setEditingDraftsById(
														(prev) => ({
															...prev,
															[String(rule.id)]:
																createEditingDraft(
																	rule,
																),
														}),
													);
													setEditingLocationRuleId(
														rule.id,
													);
												}}
											>
												<EditIcon fontSize="small" />
											</IconButton>
										</Box>

										{isEditing ? (
											<>
												{renderRuleForm({
													rule: draft,
													onChange: (key, value) =>
														setEditingDraftValue(
															rule.id,
															key,
															value,
														),
													isTimeline: false,
													isNew: false,
												})}

												<Box
													sx={{
														mt: 2,
														textAlign: "right",
														display: "flex",
														gap: 1,
														justifyContent:
															"flex-end",
													}}
												>
													<Button
														size="small"
														color="error"
														onClick={() =>
															handleDeleteRule(
																rule,
															)
														}
														disabled={
															deletingRuleId ===
															rule.id
														}
													>
														Delete
													</Button>

													<Button
														size="small"
														variant="contained"
														disableElevation
														onClick={() =>
															handleSaveEditedRule(
																rule,
															)
														}
														disabled={savingRule}
													>
														Save
													</Button>
												</Box>
											</>
										) : (
											<>
												<Typography variant="body2">
													<strong>Location:</strong>{" "}
													{rule.hospital?.name || "—"}
												</Typography>

												<Typography variant="body2">
													<strong>
														Default OOP:
													</strong>{" "}
													{rule.default_oop
														? `$${rule.default_oop}`
														: "No OOP / No gap"}
												</Typography>

												<Typography variant="body2">
													<strong>
														IFC required:
													</strong>{" "}
													{rule.ifc_required
														? "Yes"
														: "No"}
												</Typography>
											</>
										)}
									</Box>
								);
							})}
						</Box>
					</Grid>
				</Grid>
			)}
		</Box>
	);
};

export default SurgeonBillingRules;
